import { useEffect, useMemo, useState } from "react";
import { Modal, Steps, Button, message, Form } from "antd";
import Create from "./steps/Create";
import Preview from "./steps/Preview";
import Send from "./steps/Send";
import { getPropertyInfoService } from "../../../../services/easyapp.service";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
  asyncGetAdverseActionPreview,
  asyncSendAdverseActionEmails,
} from "../../../../store/easyApp/easyapp-async";

const STEPS = [
  { title: "Deny Application", component: Create },
  { title: "Preview Letters", component: Preview },
  { title: "Send & Finalize", component: Send },
];

const DEFAULT_DENIAL_REASON = "credit_criminal";

const DenyAdverseActionModal = ({
  open,
  onComplete,
  onCancel,
  application,
}: any) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canProceed, setCanProceed] = useState(false);
  const [formChangeId, setFormChangeId] = useState(0);
  const [form] = Form.useForm();

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user as any);
  const { adverseActionPdfs } = useAppSelector((state) => state.easyapp as any);

  const steps = useMemo(() => {
    const stepProps = { loading, application, formChangeId };
    return STEPS.map(({ title, component: Step }) => ({
      title,
      content: <Step {...stepProps} form={form} />,
    }));
  }, [loading, form, application, formChangeId]);

  const initializeForm = async () => {
    try {
      const allApplicants =
        application?.applicant_names?.map((a: any) => ({
          user_id: a.user_id,
          first_name: a.first_name,
          last_name: a.last_name,
          email: a.email,
          application_id: a.application_id,
        })) || [];

      form.setFieldsValue({
        denialReason: DEFAULT_DENIAL_REASON,
        selectedApplicants: allApplicants,
        manager: {
          name: user?.name || "",
          email: user?.username || "",
        },
      });

      const res: any = await getPropertyInfoService(application.ms_property_id);

      const currentValues = form.getFieldsValue(true);

      form.setFieldsValue({
        ...currentValues,
        property: {
          ...currentValues.property,
          propertyName: application?.property_name || "",
          address:
            res?.data?.ResMan?.Response?.PhysicalProperty?.Property?.PropertyID
              ?.Address || "",
        },
      });

      setError(null);
      setFormChangeId((prev) => prev + 1);
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : "Failed to load property information";
      setError(errorMsg);
      message.error(errorMsg);
    }
  };

  const validateStep0 = () => {
    const denialReason = form.getFieldValue("denialReason");
    const selectedApplicants = form.getFieldValue("selectedApplicants") || [];

    if (!denialReason || !selectedApplicants.length) return false;

    if (denialReason === "conditional_approval") {
      const depositAmount = form.getFieldValue("conditionalDepositAmount");
      return depositAmount && parseFloat(depositAmount) > 0;
    }

    return true;
  };

  const updateCanProceed = () => {
    setCanProceed(currentStep === 0 ? validateStep0() : true);
  };

  useEffect(() => {
    if (open) {
      initializeForm();
      setCurrentStep(0);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    updateCanProceed();
  }, [currentStep, formChangeId]);

  const handleFormChange = () => {
    if (currentStep === 0) {
      updateCanProceed();
      setFormChangeId((prev) => prev + 1);
    }
  };

  const resetAndClose = () => {
    setCurrentStep(0);
    setError(null);
    setCanProceed(false);
    setFormChangeId(0);
    form.resetFields();
    onCancel();
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      // Validate Step 1
      try {
        await form.validateFields([
          "denialReason",
          "selectedApplicants",
          "conditionalDepositAmount",
        ]);
        setError(null);
        setCurrentStep((s) => s + 1);
      } catch {
        setError("Please fill in all required fields correctly");
      }
    } else if (currentStep === 1) {
      // Generate PDFs before moving to Step 3
      try {
        setLoading(true);
        const formValues = form.getFieldsValue(true);

        await dispatch(
          asyncGetAdverseActionPreview({
            selectedApplicants: formValues.selectedApplicants,
            denialReason: formValues.denialReason,
            conditionalDepositAmount:
              formValues.conditionalDepositAmount || null,
            property: {
              propertyName: application?.property_name,
              address: formValues.property?.address,
            },
            manager: {
              name: user?.name || "",
              email: user?.username || "",
            },
          })
        ).unwrap();

        setError(null);
        setCurrentStep((s) => s + 1);
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : "Failed to generate adverse action previews";
        setError(errorMsg);
        message.error(errorMsg);
      } finally {
        setLoading(false);
      }
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handleFinish = async () => {
    try {
      setLoading(true);

      const formValues = form.getFieldsValue(true);

      // Prepare payload
      const payload = {
        selectedApplicants: formValues.selectedApplicants,
        denialReason: formValues.denialReason,
        conditionalDepositAmount: formValues.conditionalDepositAmount || null,
        property: {
          propertyName: application?.property_name,
          address: formValues.property?.address,
        },
        manager: {
          name: user?.name || "",
          email: user?.username || "",
        },
        applicationGroupId: application?.application_group,
        propertyName: application?.property_name,
      };

      // Create FormData and append PDFs
      const formData = new FormData();

      // Append each PDF as a separate file
      adverseActionPdfs.forEach((pdfData: any) => {
        // Convert base64 back to Blob
        const binaryString = atob(pdfData.pdf);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: "application/pdf" });

        // Create filename format: userId_applicantName.pdf
        const filename = `${pdfData.user_id}_${pdfData.applicant_name.replace(
          / /g,
          "_"
        )}.pdf`;

        formData.append("pdfFiles", blob, filename);
      });

      // Append payload as JSON string
      formData.append("payload", JSON.stringify(payload));

      await dispatch(asyncSendAdverseActionEmails(formData)).unwrap();

      message.success("Adverse action letters sent successfully!");
      resetAndClose();
      await onComplete?.();
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : "Failed to send adverse action letters";
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      {currentStep > 0 && (
        <Button onClick={() => setCurrentStep((s) => s - 1)} disabled={loading}>
          Back
        </Button>
      )}
      {currentStep < steps.length - 1 && (
        <Button
          type="primary"
          onClick={handleNext}
          loading={loading}
          disabled={loading || (currentStep === 0 && !canProceed)}
        >
          Next
        </Button>
      )}
      {currentStep === steps.length - 1 && (
        <Button
          type="primary"
          onClick={handleFinish}
          loading={loading}
          disabled={loading}
        >
          Send Letters & Deny Application
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title="Deny Application & Send Adverse Action Letters"
      onCancel={resetAndClose}
      width={1200}
      destroyOnClose
      maskClosable={false}
      footer={footer}
    >
      <Steps
        current={currentStep}
        items={steps.map(({ title }) => ({
          title,
          status: error ? "error" : undefined,
        }))}
        style={{ marginBottom: 24 }}
      />

      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 24,
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 6,
            color: "#a8071a",
            fontSize: "14px",
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      <div style={{ minHeight: 400 }}>
        <Form.Provider onFormChange={handleFormChange}>
          {steps[currentStep]?.content}
        </Form.Provider>
      </div>
    </Modal>
  );
};

export { DenyAdverseActionModal };